import React, { useCallback, useState } from 'react';

/**
 * useNumericFocusBehavior
 *
 * Shared focus/blur behavior for numeric inputs across the mobile card edit
 * dialogs and the StandardInput numeric family. Fixes the leading-zero UX
 * problem on mobile where a field showing "0" (or "$ 0") forced the user to
 * manually delete the zero, producing values like "05", "010", "01".
 *
 * Behavior:
 *  - On focus: the entire contents are selected so typing replaces them. If the
 *    current value is zero (or empty), the field is shown blank so the user can
 *    start typing immediately without a leading zero.
 *  - While focused: a local "draft" string is displayed instead of the bound
 *    value. This is what lets the blanked-on-focus state survive the parent's
 *    `Number('') === 0` coercion (which would otherwise snap the field back to
 *    "0" on the next render).
 *  - On blur: if the field was left empty, it is restored to 0 by emitting a
 *    synthetic onChange with value "0", so each field's own coercion/clamping
 *    (Number(), Math.max, Math.min, Math.round, etc.) still applies.
 *
 * Note: this hook is intended for *unformatted* numeric inputs (plain values,
 * no thousands separators). CurrencyInput / NumberInput format their own
 * display value with commas and implement the same behavior inline instead.
 */
export interface NumericFocusBehavior {
  /** The value to bind to the input (draft while focused, bound value otherwise). */
  value: string;
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export function useNumericFocusBehavior(
  boundValue: number | string | null | undefined,
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
  externalOnFocus?: (e: React.FocusEvent<HTMLInputElement>) => void,
  externalOnBlur?: (e: React.FocusEvent<HTMLInputElement>) => void,
): NumericFocusBehavior {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');

  const valueStr =
    boundValue === null || boundValue === undefined ? '' : String(boundValue);
  const numeric = Number(valueStr.replace(/,/g, ''));
  const isZero = valueStr === '' || (Number.isFinite(numeric) && numeric === 0);

  const onFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      // Zero/empty -> start blank (no leading zero to delete).
      // Non-zero -> keep the value but select it so typing replaces it.
      setDraft(isZero ? '' : valueStr);
      const el = e.target;
      // Defer so the value is committed before selecting (best-effort on mobile;
      // some browsers disallow select() on type="number").
      setTimeout(() => {
        try {
          el.select();
        } catch {
          /* ignore */
        }
      }, 0);
      externalOnFocus?.(e);
    },
    [isZero, valueStr, externalOnFocus],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDraft(e.target.value);
      onChange?.(e);
    },
    [onChange],
  );

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      if (draft === '') {
        // Field left empty -> restore to 0 through the field's own onChange so
        // its coercion/clamping still applies.
        const synthetic = {
          ...e,
          target: { ...e.target, value: '0' },
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        onChange?.(synthetic);
      }
      externalOnBlur?.(e);
    },
    [draft, onChange, externalOnBlur],
  );

  return { value: focused ? draft : valueStr, onFocus, onChange: handleChange, onBlur };
}
